'use client'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface HeroProps {
  title: string
  subtitle?: string
  ctaText?: string
  ctaLink?: string
  secondaryCta?: {
    text: string
    link: string
  }
  backgroundImage?: string
  className?: string
  showScrollIndicator?: boolean
}

export default function Hero({
  title,
  subtitle,
  ctaText,
  ctaLink,
  secondaryCta,
  backgroundImage,
  className = '',
  showScrollIndicator = false,
}: HeroProps) {
  return (
    <section className={`relative min-h-screen flex items-center overflow-hidden pt-24 ${className}`}>
      {/* Enhanced Background with Aurora and Grid Pattern */}
      <div className="absolute inset-0 z-0">
        {backgroundImage ? (
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover opacity-20"
            priority
          />
        ) : (
          <>
            {/* Aurora gradient background */}
            <div className="absolute inset-0 bg-aurora opacity-40"></div>
            {/* Grid pattern overlay */}
            <div className="absolute inset-0 bg-grid-pattern bg-grid-pattern opacity-30"></div>
            {/* Base gradient */}
            <div className="absolute inset-0 section-bg-gradient"></div>
          </>
        )}
        {/* Enhanced radial gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(61,120,237,0.15),transparent_70%)]"></div>
        {/* Additional depth layer */}
        <div className="absolute inset-0 bg-[radial-gradient(600px_400px_at_50%_20%,rgba(90,110,255,0.18),transparent_60%)]"></div>
      </div>

      {/* Content */}
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 animate-fade-in">
            <div>
              <div className="brand-chip mb-6 transition-transform duration-300 hover:scale-105">
                PREMIUM LIVE RESIN
              </div>
              <h1 className="heading-xl text-hierarchy-1 mb-6 leading-tight">
                {title.includes(' ') ? (
                  <>
                    <span className="bg-gradient-to-r from-white via-white to-slate-200 bg-clip-text text-transparent">
                      {title.split(' ').slice(0, -2).join(' ')}
                    </span>{' '}
                    <span className="italic-accent bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent">
                      {title.split(' ').slice(-2).join(' ')}
                    </span>
                  </>
                ) : (
                  <span className="bg-gradient-to-r from-white via-white to-slate-200 bg-clip-text text-transparent">
                    {title}
                  </span>
                )}
              </h1>
              {subtitle && (
                <p className="text-lg text-hierarchy-2 max-w-xl leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              {ctaText && ctaLink && (
                <Button 
                  asChild 
                  variant="premium" 
                  size="lg"
                  className="transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1 hover:shadow-elev-2 relative overflow-hidden group"
                >
                  <Link href={ctaLink}>
                    <span className="relative z-10">{ctaText}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-eskerium-blue to-eskerium-gold opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg"
                  className="transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1 card-glass border-white/20 hover:border-white/30 hover:bg-white/10"
                >
                  <Link href={secondaryCta.link}>
                    {secondaryCta.text}
                  </Link>
                </Button>
              )}
            </div>
          </div>
          
          <div className="relative h-[500px] animate-fade-in-delayed">
            {/* Enhanced floating background element */}
            <div className="absolute top-0 right-0 w-[90%] h-[90%] bg-gradient-to-br from-eskerium-blue/20 via-eskerium-gold/20 to-eskerium-blue/20 rounded-3xl animate-float opacity-60"></div>
            
            {/* Main glass card with enhanced effects */}
            <div className="card-glass gradient-border absolute inset-0 overflow-hidden shadow-elev-2 hover:shadow-elev-2 transition-all duration-500 hover:-translate-y-2">
              <Image 
                src={backgroundImage || "/images/extract-close-1.jpg"} 
                alt="Premium Hemp Extract" 
                fill
                className="object-cover opacity-90 transition-opacity duration-500 hover:opacity-95"
              />
              {/* Overlay gradient for better text contrast */}
              <div className="absolute inset-0 bg-gradient-to-t from-eskerium-dark/40 via-transparent to-transparent"></div>
            </div>

            {/* Floating accent elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-gradient-to-r from-eskerium-blue to-eskerium-gold animate-pulse opacity-60"></div>
            <div className="absolute -bottom-4 -right-4 w-6 h-6 rounded-full bg-gradient-to-r from-eskerium-gold to-eskerium-blue animate-pulse opacity-40" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </div>
      
      {showScrollIndicator && (
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-full text-center animate-fade-in-delayed">
          <button 
            onClick={() => document.getElementById('content')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex flex-col items-center text-white/70 hover:text-white transition-all duration-300 ease-gentle mx-auto hover:scale-105 group"
          >
            <span className="text-sm uppercase tracking-widest mb-2 font-light group-hover:text-eskerium-gold transition-colors duration-300">Explore</span>
            <ArrowDown size={20} className="animate-bounce group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </div>
      )}
    </section>
  )
}